import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAdminAuth } from '../../store/slices/adminAuthSlice';
import {
  fetchTransactionsThunk,
  setFilters,
  setPage,
  selectTransactions,
  selectTransactionMeta,
  selectTransactionFilters,
  selectTransactionPage,
  selectTransactionLoading,
  selectTransactionError
} from '../../store/slices/transactionSlice';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, MenuItem, Stack, CircularProgress, Alert, useMediaQuery, Skeleton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

const statusOptions = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  // Add more as needed
];

const ITEMS_PER_PAGE = 20;

function SkeletonTableRows({ rows = 8, columns = 7 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: columns }).map((_, j) => (
        <TableCell key={j}><Skeleton variant="rectangular" height={24} /></TableCell>
      ))}
    </TableRow>
  ));
}

const TransactionHistory = () => {
  const dispatch = useDispatch();
  const { token: adminToken } = useSelector(selectAdminAuth);
  const transactions = useSelector(selectTransactions);
  const meta = useSelector(selectTransactionMeta);
  const filters = useSelector(selectTransactionFilters);
  const page = useSelector(selectTransactionPage);
  const loading = useSelector(selectTransactionLoading);
  const error = useSelector(selectTransactionError);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // useEffect(() => {
  //   // Only fetch if transactions are not already loaded for this filter/page
  //   if (adminToken && transactions.length === 0 && !loading) {
  //     dispatch(fetchTransactionsThunk({ filters, page }));
  //   }
  //   // eslint-disable-next-line
  // }, [adminToken]);

  useEffect(() => {
    dispatch(fetchTransactionsThunk({ filters, page }));
  }, [dispatch]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFilters({ ...filters, [name]: value }));
  };

  const handleSearch = () => {
    dispatch(setPage(0));
    dispatch(fetchTransactionsThunk({ filters, page: 0 }));
  };

  const handleReset = () => {
    const resetFilters = {
      email: '',
      status: '',
      source: '',
      min_amount: '',
      max_amount: '',
      start_date: '',
      end_date: '',
    };
    dispatch(setFilters(resetFilters));
    dispatch(setPage(0));
    dispatch(fetchTransactionsThunk({ filters: resetFilters, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
    dispatch(fetchTransactionsThunk({ filters, page: newPage }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Transaction History
      </Typography>

      <Paper sx={{ p: isMobile ? 1 : 2, mb: 3, overflowX: isMobile ? 'auto' : 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1.5 : 2,
            alignItems: isMobile ? 'stretch' : 'center',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            pb: isMobile ? 1 : 0,
            minWidth: isMobile ? 600 : 'unset',
          }}
        >
          <TextField label="User Email" name="email" value={filters.email} onChange={handleFilterChange} size="small" fullWidth={isMobile} sx={{ minWidth: isMobile ? 220 : 140 }} />
          <TextField label="Status" name="status" value={filters.status} onChange={handleFilterChange} select size="small" fullWidth={isMobile} sx={{ minWidth: isMobile ? 120 : 120 }}>
            {statusOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Source" name="source" value={filters.source} onChange={handleFilterChange} size="small" fullWidth={isMobile} sx={{ minWidth: isMobile ? 120 : 120 }} />
          <TextField label="Min Amount" name="min_amount" value={filters.min_amount} onChange={handleFilterChange} size="small" type="number" fullWidth={isMobile} sx={{ minWidth: isMobile ? 100 : 100 }} />
          <TextField label="Max Amount" name="max_amount" value={filters.max_amount} onChange={handleFilterChange} size="small" type="number" fullWidth={isMobile} sx={{ minWidth: isMobile ? 100 : 100 }} />
          <TextField label="Start Date" name="start_date" value={filters.start_date} onChange={handleFilterChange} size="small" type="date" InputLabelProps={{ shrink: true }} fullWidth={isMobile} sx={{ minWidth: isMobile ? 120 : 120 }} />
          <TextField label="End Date" name="end_date" value={filters.end_date} onChange={handleFilterChange} size="small" type="date" InputLabelProps={{ shrink: true }} fullWidth={isMobile} sx={{ minWidth: isMobile ? 120 : 120 }} />
          <Button variant="contained" onClick={handleSearch} sx={{ minWidth: isMobile ? 100 : 100, mt: isMobile ? 1 : 0, borderRadius: 1, fontSize: 14}}>Search</Button>
          <Button variant="outlined" onClick={handleReset} sx={{ minWidth: isMobile ? 100 : 100, mt: isMobile ? 1 : 0, borderRadius: 1, fontSize: 14 }}>Reset</Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>ID</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>User Email</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Currency</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Source</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SkeletonTableRows rows={Math.max(2, Math.min(transactions.length || 1, 8))} columns={7} />
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>ID</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>User Email</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Currency</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Source</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No transactions found.</TableCell>
                </TableRow>
              ) : (
                transactions.map(tx => (
                  <TableRow key={tx.id}>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>{tx.user_email}</TableCell>
                    <TableCell>{tx.amount}</TableCell>
                    <TableCell>{tx.currency}</TableCell>
                    <TableCell>{tx.status}</TableCell>
                    <TableCell>{tx.source}</TableCell>
                    <TableCell>{dayjs(tx.created_at).format('MMM D, YYYY, h:mm A')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination controls */}
      <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center" mt={2}>
        <Button
          variant="outlined"
          disabled={page === 0}
          onClick={() => handlePageChange(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          Page {page + 1} {meta.total ? `/ ${Math.ceil(meta.total / ITEMS_PER_PAGE)}` : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={meta.total ? (page + 1) * ITEMS_PER_PAGE >= meta.total : true}
          onClick={() => handlePageChange(page + 1)}
        >
          Next
        </Button>
      </Stack>
    </Box>
  );
};

export default TransactionHistory; 